import { zodResolver } from '@hookform/resolvers/zod';
import type { PersonDetail } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { PickerSheet } from '../components/PickerSheet';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, ListGroup, SectionLabel } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { usePeople, usePerson } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { formatAmount } from '../lib/money';
import { DebtRow } from './DebtsPage';
import { type PersonFormInput, type PersonFormOutput, personForm } from './PeoplePage';

/** One person: what we owe them and they owe us per currency, their debts, edit and merge. */
export function PersonPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const me = useMe();
  const person = usePerson(id);
  if (!me.data || person.isPending) return <p role="status">{t('app.loading')}</p>;
  if (person.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('people.title')} back="/people" />
        <Card role="alert" className="flex flex-col gap-3">
          <FormError error={person.error} />
          <Button variant="secondary" onClick={() => void person.refetch()}>
            {t('common.retry')}
          </Button>
        </Card>
      </div>
    );
  }
  return <Person person={person.data} baseCurrency={me.data.family.baseCurrency} />;
}

function Person({ person, baseCurrency }: { person: PersonDetail; baseCurrency: string }) {
  const { t, i18n } = useTranslation();
  const manage = useCan('people.manage');
  const outside = useCan('debts.outside');
  const invalidate = useInvalidateLedger();
  const [editing, setEditing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeInto, setMergeInto] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState('');
  const candidates = usePeople(merging ? search.trim() : '', false);

  const archive = useMutation({
    mutationFn: () =>
      person.archivedAt ? api.people.unarchive(person.id) : api.people.archive(person.id),
    onSuccess: invalidate,
  });
  const merge = useMutation({
    mutationFn: (intoId: string) => api.people.merge(person.id, { intoId }),
    onSuccess: async () => {
      setMergeInto(null);
      await invalidate();
    },
  });

  const money = (amount: string, currency: string) => formatAmount(amount, currency, i18n.language);
  const open = person.debts.filter((d) => !d.settled);
  const settled = person.debts.filter((d) => d.settled);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={person.name} back="/people" />
      {person.archivedAt && <p className="px-1 text-sm text-muted">{t('people.archived')}</p>}
      {(person.phone || person.note) && !editing && (
        <Card className="flex flex-col gap-1">
          {person.phone && (
            <a href={`tel:${person.phone}`} dir="ltr" className="w-fit text-accent">
              {person.phone}
            </a>
          )}
          {person.note && <p className="text-sm text-muted">{person.note}</p>}
        </Card>
      )}

      {person.byCurrency.length > 0 && (
        <Card className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1 rounded-2xl bg-surface-2 p-3">
              <span className="text-[13px] text-muted">{t('people.weOwe')}</span>
              {person.byCurrency.map((b) => (
                <span key={b.currency} dir="ltr" className="text-[15px] font-semibold tabular-nums">
                  {money(b.weOwe, b.currency)}
                </span>
              ))}
            </div>
            <div className="flex flex-col gap-1 rounded-2xl bg-surface-2 p-3">
              <span className="text-[13px] text-muted">{t('people.owedToUs')}</span>
              {person.byCurrency.map((b) => (
                <span key={b.currency} dir="ltr" className="text-[15px] font-semibold tabular-nums">
                  {money(b.owedToUs, b.currency)}
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      {outside && !person.archivedAt && (
        <Link
          to={`/debts/new?person=${encodeURIComponent(person.id)}&name=${encodeURIComponent(person.name)}`}
          className={buttonVariants()}
        >
          {t('people.newDebt', { name: person.name })}
        </Link>
      )}

      <section className="flex flex-col gap-2">
        <SectionLabel>{t('debts.title')}</SectionLabel>
        {person.debts.length === 0 ? (
          <p className="px-1 text-sm text-muted">{t('people.noDebts')}</p>
        ) : (
          <ListGroup>
            {[...open, ...settled].map((d) => (
              <li key={d.id}>
                <DebtRow debt={d} baseCurrency={baseCurrency} />
              </li>
            ))}
          </ListGroup>
        )}
      </section>

      {manage && (
        <section className="flex flex-col gap-2">
          {editing ? (
            <EditPerson person={person} onDone={() => setEditing(false)} />
          ) : (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('people.edit')}
            </Button>
          )}
          {!person.archivedAt && (
            <>
              <Button variant="secondary" onClick={() => setMerging(true)}>
                {t('people.merge')}
              </Button>
              <p className="px-1 text-xs text-muted">{t('people.mergeHint')}</p>
            </>
          )}
          {mergeInto && (
            <Card className="flex flex-col gap-3">
              <p className="font-medium">
                {t('people.confirmMerge', { from: person.name, into: mergeInto.name })}
              </p>
              <FormError error={merge.error} />
              <Button disabled={merge.isPending} onClick={() => merge.mutate(mergeInto.id)}>
                {merge.isPending ? t('common.saving') : t('people.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setMergeInto(null)}>
                {t('common.cancel')}
              </Button>
            </Card>
          )}
          <FormError error={archive.error} />
          <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate()}>
            {person.archivedAt ? t('people.unarchive') : t('people.archive')}
          </Button>
        </section>
      )}

      {merging && (
        <PickerSheet
          title={t('people.pickMerge')}
          search={{ value: search, onChange: setSearch, label: t('people.search') }}
          options={(candidates.data?.items ?? [])
            .filter((p) => p.id !== person.id)
            .map((p) => ({ value: p.id, label: p.name }))}
          empty={t('people.empty')}
          onPick={(id) => {
            const into = candidates.data?.items.find((p) => p.id === id);
            if (into) setMergeInto({ id: into.id, name: into.name });
            setMerging(false);
          }}
          onClose={() => setMerging(false)}
        />
      )}
    </div>
  );
}

function EditPerson({ person, onDone }: { person: PersonDetail; onDone: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateLedger();
  const form = useForm<PersonFormInput, unknown, PersonFormOutput>({
    resolver: zodResolver(personForm),
    defaultValues: { name: person.name, phone: person.phone ?? '', note: person.note ?? '' },
  });
  const save = useMutation({
    mutationFn: (v: PersonFormOutput) =>
      api.people.update(person.id, {
        name: v.name,
        phone: v.phone || null,
        note: v.note || null,
      }),
    onSuccess: async () => {
      await invalidate();
      onDone();
    },
  });
  const errors = form.formState.errors;
  return (
    <Card>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={form.handleSubmit((v) => save.mutate(v))}
      >
        <FormError error={save.error} />
        <Field label={t('people.name')} error={errors.name}>
          <Input autoComplete="off" {...form.register('name')} />
        </Field>
        <Field label={t('people.phone')} hint={t('people.optional')} error={errors.phone}>
          <Input type="tel" autoComplete="off" dir="ltr" {...form.register('phone')} />
        </Field>
        <Field label={t('people.note')} hint={t('people.optional')} error={errors.note}>
          <Input autoComplete="off" {...form.register('note')} />
        </Field>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? t('common.saving') : t('people.save')}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </form>
    </Card>
  );
}
